/**
 * 帳票作成画面（S-14/15・S-63・S-64・S-70・S-71）の種別ごとの設定。担当: G4
 * 一覧・作成・詳細の画面部品（SnapshotListPage / SnapshotDetailPage）はこの設定で出し分ける。
 */
import type { ReactNode } from 'react';
import type { Column } from '@/components';
import type { OrganizationType } from '@/auth/types';
import { formatNumber } from '@/lib/format';
import { toWareki } from '@/lib/wareki';
import type { SnapshotListItem, SnapshotType } from './api';

type Row = Record<string, unknown>;
const t = (v: unknown) => (v == null ? '' : String(v));
const n = (v: unknown, digits = 0) => (v == null || v === '' ? '' : formatNumber(Number(v), digits));
const mono = (v: unknown) => <span className="bs-num">{t(v)}</span>;

export interface SectionDef {
  key: string;
  title: string;
  columns: Column<Row>[];
}

export interface OutputDef {
  label: string;
  reportId: string;
  section?: 'sighting' | 'gear';
}

export interface FormTypeConfig {
  type: SnapshotType;
  /** 年度配下のパス */
  path: string;
  title: string;
  /** 画面ID・帳票ID（説明表示用） */
  screenIds: string;
  description: string;
  viewRoles: readonly OrganizationType[];
  /** 作成条件の種類 */
  createKind: 'roster' | 'monitoring1' | 'monitoring2' | 'monitoring3' | 'dispatch';
  listColumns: Column<SnapshotListItem>[];
  outputs: OutputDef[];
  /** 編集可能な特記事項（header のキー） */
  notes: { key: string; label: string }[];
  sections: SectionDef[];
  /** 詳細のヘッダに出す要約 */
  summary: (header: Row) => { label: string; value: ReactNode }[];
}

const ALL: readonly OrganizationType[] = ['基金', '漁協', '水産庁', '沖縄県'];
const FUND_COOP: readonly OrganizationType[] = ['基金', '漁協'];

const periodCol: Column<SnapshotListItem> = {
  key: 'periodFrom',
  header: '期間',
  sortable: true,
  width: 230,
  render: (r) => (
    <span className="bs-num">
      {r.periodFrom ?? ''} 〜 {r.periodTo ?? ''}
    </span>
  ),
};
const opCol: Column<SnapshotListItem> = { key: 'businessOperatorName', header: '事業実施者', sortable: true, width: 160 };
const countCol: Column<SnapshotListItem> = { key: 'lineCount', header: '明細', numeric: true, width: 80 };
const createdCol: Column<SnapshotListItem> = {
  key: 'createdAt',
  header: '作成日時',
  sortable: true,
  width: 160,
  render: (r) => <span className="bs-num">{r.createdAt.slice(0, 16).replace('T', ' ')}</span>,
};

const MONTH_DEKADS = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3].flatMap((m) =>
  [1, 2, 3].map((d) => ({ key: `${m}-${d}`, header: `${m}月${['初', '中', '下'][d - 1]}` })),
);

export const FORM_CONFIGS: Record<string, FormTypeConfig> = {
  roster: {
    type: '参加船名簿',
    path: 'forms/roster',
    title: '参加船名簿（別記様式第１号）',
    screenIds: 'S-14・S-15 / R-01',
    description: '基準日時点の参加船名簿（計画：船長の承認期間が基準日を含む船／実績：調査期間が基準日を含む速報）を作成し、別記様式第１号を出力します。',
    viewRoles: ALL,
    createKind: 'roster',
    listColumns: [
      opCol,
      { key: 'variant', header: '計画／実績', sortable: true, width: 100 },
      { key: 'baseDate', header: '基準日', sortable: true, width: 200, render: (r) => (r.baseDate ? `${r.baseDate}（${toWareki(r.baseDate)}）` : '') },
      { key: 'totalVessels', header: '合計船隻', numeric: true, width: 90, accessor: (r) => r.header.totalVessels },
      { key: 'totalPersons', header: '合計人数', numeric: true, width: 90, accessor: (r) => r.header.totalPersons },
      createdCol,
    ],
    outputs: [{ label: '帳票出力（別記様式第１号）', reportId: 'R-01' }],
    notes: [],
    summary: (h) => [
      { label: '合計船隻', value: `${n(h.totalVessels)} 隻` },
      { label: '合計人数', value: `${n(h.totalPersons)} 人` },
    ],
    sections: [
      {
        key: 'roster',
        title: '参加船名簿',
        columns: [
          { key: 'no', header: 'No', width: 52, numeric: true },
          { key: 'ownerName', header: '氏名又は名称', width: 140 },
          { key: 'vesselName', header: '船名', width: 130 },
          { key: 'captainName', header: '船長氏名', width: 120 },
          { key: 'registrationNo', header: '登録番号', width: 130, render: (r) => mono(r.registrationNo) },
          { key: 'tonnage', header: 'トン数', numeric: true, width: 80, render: (r) => n(r.tonnage, 2) },
          { key: 'fishingMethods', header: '漁業種類', width: 120 },
          { key: 'boardingCount', header: '乗船人員', numeric: true, width: 84 },
          { key: 'phone', header: '船舶電話', width: 130, render: (r) => mono(r.phone) },
          { key: 'totalBoardingCount', header: '全乗船人員', numeric: true, width: 90 },
          { key: 'note', header: '備考' },
        ],
      },
    ],
  },
  monitoring1: {
    type: '監視実績報告書1',
    path: 'forms/monitoring-report-1',
    title: '監視実績報告書①（視認状況・漁場調査等）',
    screenIds: 'S-70 / R-05・R-06',
    description: '年度（4/1〜翌3/31）の視認状況報告・漁場調査等（漁具情報）を集計し、別記様式第３－１号①を出力します。特記事項は作成後に編集できます。',
    viewRoles: ALL,
    createKind: 'monitoring1',
    listColumns: [opCol, periodCol, countCol, createdCol],
    outputs: [
      { label: '視認状況（R-05）', reportId: 'R-05', section: 'sighting' },
      { label: '漁場調査等（R-06）', reportId: 'R-06', section: 'gear' },
    ],
    notes: [
      { key: 'sightingNotes', label: '特記事項（視認状況）' },
      { key: 'gearNotes', label: '特記事項（漁場調査等）' },
    ],
    summary: () => [],
    sections: [
      {
        key: 'sighting',
        title: '視認状況',
        columns: [
          { key: 'no', header: '№', width: 52, numeric: true },
          { key: 'dateLabel', header: '視認月日', width: 90, render: (r) => mono(r.dateLabel) },
          { key: 'time', header: '時刻', width: 64, render: (r) => mono(r.time) },
          { key: 'latLon', header: '緯度・経度', width: 260, render: (r) => mono(r.latLon) },
          { key: 'activity', header: '操業・航海・漂泊', width: 90 },
          { key: 'heading', header: '進行方向等', width: 90 },
          { key: 'nationality', header: '国籍', width: 64 },
          { key: 'foreignVesselName', header: '船名', width: 110 },
          { key: 'registrationNo', header: '登録番号', width: 100 },
          { key: 'approvalNo', header: '承認番号', width: 90 },
          { key: 'fishingType', header: '漁業種類', width: 84 },
          { key: 'vesselCount', header: '隻数', numeric: true, width: 60 },
          { key: 'reported', header: '通報の有無', width: 76, align: 'center' },
          { key: 'surveyVesselName', header: '調査船名', width: 110 },
          { key: 'note', header: '備考', width: 160 },
        ],
      },
      {
        key: 'gear',
        title: '漁場調査等（漁具情報）',
        columns: [
          { key: 'no', header: '№', width: 52, numeric: true },
          { key: 'dateLabel', header: '発見年月日', width: 96, render: (r) => mono(r.dateLabel) },
          { key: 'time', header: '時刻', width: 64, render: (r) => mono(r.time) },
          { key: 'latLon', header: '緯度・経度', width: 260, render: (r) => mono(r.latLon) },
          { key: 'gearType', header: '漁具種類', width: 120 },
          { key: 'nationality', header: '国籍', width: 64 },
          { key: 'note', header: '備考（漁具の特徴及び調査船名など）' },
        ],
      },
    ],
  },
  monitoring2: {
    type: '監視実績報告書2',
    path: 'forms/monitoring-report-2',
    title: '監視実績報告書②（漁獲データ）',
    screenIds: 'S-63 / R-07',
    description: '調査日の期間と調査航海期間が重なる漁場情報の明細から、別記様式第３－１号②（漁場調査による漁場形成状況）を作成します。',
    viewRoles: ALL,
    createKind: 'monitoring2',
    listColumns: [opCol, periodCol, countCol, createdCol],
    outputs: [{ label: '帳票出力（別記様式第３－１号②）', reportId: 'R-07' }],
    notes: [{ key: 'specialNotes', label: '特記事項' }],
    summary: () => [],
    sections: [
      {
        key: 'catch',
        title: '漁獲データ',
        columns: [
          { key: 'no', header: '№', width: 52, numeric: true },
          { key: 'registrationNo', header: '漁船登録番号', width: 120, render: (r) => mono(r.registrationNo) },
          { key: 'vesselName', header: '船名', width: 110 },
          { key: 'businessOperatorName', header: '事業実施者名', width: 110 },
          { key: 'surveyDate', header: '調査日', width: 100, render: (r) => mono(r.surveyDate) },
          { key: 'lat', header: '緯度', width: 110, render: (r) => mono(r.latDeg == null ? '' : `${t(r.latDeg)}°${t(r.latMin)}′${t(r.latSec)}″`) },
          { key: 'lon', header: '経度', width: 120, render: (r) => mono(r.lonDeg == null ? '' : `${t(r.lonDeg)}°${t(r.lonMin)}′${t(r.lonSec)}″`) },
          { key: 'weather', header: '天候', width: 64 },
          { key: 'wind', header: '風向・風力', width: 90, render: (r) => [t(r.windDirection), t(r.windForce)].filter(Boolean).join('・') },
          { key: 'waterTemp', header: '表面水温', numeric: true, width: 76, render: (r) => n(r.waterTemp, 1) },
          { key: 'depth', header: '水深', numeric: true, width: 70 },
          { key: 'current', header: '潮流', width: 70 },
          { key: 'sonarReaction', header: '魚探反応', numeric: true, width: 76 },
          { key: 'schoolCondition', header: '魚群の状況', width: 110 },
          { key: 'fishingMethod', header: '漁法', width: 90 },
          { key: 'standardName', header: '標準和名', width: 100 },
          { key: 'localName', header: '地方名', width: 90 },
          { key: 'weight', header: '数量(kg)', numeric: true, width: 80, render: (r) => n(r.weight, 2) },
          { key: 'count', header: '本数', numeric: true, width: 64 },
          { key: 'note', header: '備考', width: 120 },
        ],
      },
    ],
  },
  monitoring3: {
    type: '監視実績報告書3',
    path: 'forms/monitoring-report-3',
    title: '監視実績報告書③（漁獲調査水揚実績）',
    screenIds: 'S-64 / R-08',
    description: '操業期間と調査航海期間が重なる漁場情報の水揚実績から、別記様式第３－１号③を作成します（期間が重複する作成済みデータがある場合は作成できません）。',
    viewRoles: FUND_COOP,
    createKind: 'monitoring3',
    listColumns: [
      opCol,
      periodCol,
      countCol,
      { key: 'totalLanding', header: '水揚合計額', numeric: true, width: 120, accessor: (r) => r.header.totalLanding },
      createdCol,
    ],
    outputs: [{ label: '帳票出力（別記様式第３－１号③）', reportId: 'R-08' }],
    notes: [],
    summary: (h) => [
      { label: '水揚合計額（税込）', value: `${n(h.totalLanding)} 円` },
      { label: '出荷控除額（税込）', value: `${n(h.totalDeduction)} 円` },
      { label: '手取金額（税抜）', value: `${n(h.totalNet)} 円` },
      { label: '帳票の操業期間', value: h.displayFrom ? `${toWareki(t(h.displayFrom))}〜${toWareki(t(h.displayTo))}` : '' },
    ],
    sections: [
      {
        key: 'landing',
        title: '水揚実績',
        columns: [
          { key: 'no', header: '№', width: 52, numeric: true },
          { key: 'vesselName', header: '船名' },
          { key: 'landingDate', header: '水揚年月日', width: 130, render: (r) => mono(r.landingYear ? `${t(r.landingYear)}年${t(r.landingMonth)}月${t(r.landingDay)}日` : '') },
          { key: 'fishingGround', header: '調査・操業漁場（農林漁区番号）', width: 200 },
          { key: 'landingTotalInclTax', header: '水揚合計額（税込）', numeric: true, width: 150 },
          { key: 'shippingDeductionInclTax', header: '出荷控除額（税込）', numeric: true, width: 150 },
          { key: 'netAmountExclTax', header: '手取金額（税抜）', numeric: true, width: 150 },
        ],
      },
    ],
  },
  dispatch: {
    type: '調査船出動計画表',
    path: 'forms/dispatch-plan',
    title: '調査船出動計画表（予定隻日）',
    screenIds: 'S-71 / R-16',
    description: '速報の調査日（調査時間 > 0）を月・旬・トン数区分で集計し、延べ隻日と四半期累計の調査船出動計画表を作成します。',
    viewRoles: FUND_COOP,
    createKind: 'dispatch',
    listColumns: [
      opCol,
      { key: 'variant', header: '四半期', sortable: true, width: 110 },
      { key: 'q4', header: '第4累計（計）', numeric: true, width: 120, accessor: (r) => (r.header.cumulative as Row | undefined)?.q4 },
      createdCol,
    ],
    outputs: [{ label: '帳票出力（調査船出動計画表）', reportId: 'R-16' }],
    notes: [],
    summary: (h) => {
      const c = (h.cumulative ?? {}) as Row;
      return [
        { label: '第1累計', value: n(c.q1) },
        { label: '第2累計', value: n(c.q2) },
        { label: '第3累計', value: n(c.q3) },
        { label: '第4累計', value: n(c.q4) },
        { label: '参加船名簿隻数 × 年間日数', value: `${n(h.vesselCount)} 隻 × ${n(h.annualDays)} 日 = ${n(Number(h.vesselCount ?? 0) * Number(h.annualDays ?? 0))} 隻日` },
      ];
    },
    sections: [
      {
        key: 'dispatch',
        title: '月・旬別 延べ隻日',
        columns: [
          { key: 'tonClass', header: 'トン数区分', width: 130 },
          ...MONTH_DEKADS.map((d) => ({
            key: d.key,
            header: d.header,
            numeric: true,
            width: 64,
            accessor: (r: Row) => (r.dekads as Row | undefined)?.[d.key],
          })),
          { key: 'q1', header: '第1累計', numeric: true, width: 76, accessor: (r: Row) => (r.cumulative as Row | undefined)?.q1 },
          { key: 'q2', header: '第2累計', numeric: true, width: 76, accessor: (r: Row) => (r.cumulative as Row | undefined)?.q2 },
          { key: 'q3', header: '第3累計', numeric: true, width: 76, accessor: (r: Row) => (r.cumulative as Row | undefined)?.q3 },
          { key: 'q4', header: '第4累計', numeric: true, width: 76, accessor: (r: Row) => (r.cumulative as Row | undefined)?.q4 },
        ],
      },
    ],
  },
};
